import { useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import Chips from 'components/field/Chips';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import TimeInput from 'components/field/TimeInput';
import Icon from 'components/global/Icon';
import ModuleToolTipContainer from 'components/global/ModuleToolTipContainer';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';
import Table from 'components/table/Table';

import useAccessControl from 'hooks/useAccessControl';
import useAddressData from 'hooks/useAddressData';
import useAlert from 'hooks/useAlert';
import useMediaQuery from 'hooks/useMediaQuery';

import { AUTOCOMPLETE, ENUMS } from 'services/consts';

import { Breakpoints } from 'utils/const.styles';
import PATHS from 'utils/paths';

import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';

import * as S from './AutomationConditionsTable.styled';

export const AutomationConditionsConfig = [
	{
		name: 'brandIds',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.BRANDS,
		columns: [{ accessor: 'Name', width: 200 }],
		selection: ['Name'],
		nameV1: 'brands',
		multipleSelection: true
	},
	{ name: 'category', type: 'equal', component: 'string' },
	{ name: 'sku', type: 'equal', component: 'string' },
	{ name: 'packageSize', type: 'equal', component: 'string', nameV1: 'size' },
	{
		name: 'itemQuantity',
		type: 'less',
		component: 'integer',
		measurementLabel: 'pcs',
		nameV1: 'quantity'
	},
	{
		name: 'shipmentWeight',
		type: 'less',
		component: 'integer',
		measurementLabel: 'kg',
		nameV1: 'maxWeight',
		nameV12: 'minWeight'
	},
	{
		name: 'packageCount',
		type: 'equalLess',
		component: 'integer',
		measurementLabel: 'pcs'
	},
	{
		name: 'codValue',
		type: 'less',
		component: 'integer',
		measurementLabel: '',
		nameV1: 'maxCodValue',
		nameV12: 'minCodValue'
	},
	{
		name: 'orderValue',
		type: 'less',
		component: 'integer',
		measurementLabel: '',
		nameV1: 'maxOrderValue',
		nameV12: 'minOrderValue'
	},
	{ name: 'timeSinceCreation', type: 'less', component: 'integer', measurementLabel: 'min' },
	{
		name: 'drivingDistance',
		type: 'less',
		component: 'integer',
		measurementLabel: 'km',
		nameV1: 'maxDrivingDistance',
		nameV12: 'minDrivingDistance'
	},
	{
		name: 'driverTask',
		type: 'less',
		component: 'integer',
		measurementLabel: 'pcs',
		nameV1: 'maxDriverTask',
		nameV12: 'minDriverTask'
	},
	{
		name: 'paymentTypes',
		type: 'equal',
		component: 'enum',
		enumName: ENUMS.PAYMENT_METHOD_FOR_LIST,
		multipleSelection: true
	},
	{
		name: 'originCity',
		type: 'equalCustom',
		component: 'cityStr',
		customComponent: 'chips'
	},
	{
		name: 'destinationCity',
		type: 'equalCustom',
		component: 'cityStr',
		customComponent: 'chips'
	},
	{ name: 'originCountry', type: 'equal', component: 'countryStr' },
	{ name: 'destinationCountry', type: 'equal', component: 'countryStr' },
	{ name: 'shippingFee', type: 'less', component: 'integer', measurementLabel: '' },
	{ name: 'webstoreName', type: 'equal', component: 'string' },
	{ name: 'shippingDescription', type: 'equalContaines', component: 'chips' },
	{
		name: 'ref1',
		type: 'equal',
		component: 'string'
	},
	{ name: 'ref2', type: 'equal', component: 'string' },
	{ name: 'ref3', type: 'equal', component: 'string' },
	{ name: 'hasDeliveryTimeSlot', type: null, component: 'enum', enumName: ENUMS.YES_NO, multipleSelection: false },
	{ name: 'dayOfWeek', type: 'equal', component: 'enum', enumName: ENUMS.DAY_OF_WEEK, multipleSelection: true },
	{ name: 'withinCity', type: null, component: 'enum', enumName: ENUMS.YES_NO, multipleSelection: false },
	{ name: 'withinCountry', type: null, component: 'enum', enumName: ENUMS.YES_NO, multipleSelection: false },
	{ name: 'shipmentCreateFromTime', type: null, component: 'time' },
	{ name: 'shipmentCreateToTime', type: null, component: 'time' },
	{
		name: 'branchId',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.BRANCHES,
		columns: [
			{ accessor: 'Name', width: 200 },
			{ accessor: 'Code', width: 150 }
		],
		selection: ['Name'],
		multipleSelection: true
	},
	{
		name: 'warehouseId',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.WAREHOUSES,
		columns: [
			{ accessor: 'Name', width: 200 },
			{ accessor: 'Code', width: 150 }
		],
		selection: ['Name'],
		multipleSelection: true
	},
	{
		name: 'areaId',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.AREA,
		columns: [
			{ accessor: 'Name', width: 200 },
			{ accessor: 'CityName', width: 200 }
		],
		selection: ['Name'],
		multipleSelection: true
	},
	{
		name: 'dangerousGoods',
		type: null,
		component: 'enum',
		enumName: ENUMS.YES_NO,
		multipleSelection: false
	},
	{
		name: 'temperatureControl',
		type: null,
		component: 'enum',
		enumName: ENUMS.YES_NO,
		multipleSelection: false
	},
	{ name: 'withinDeliveryTimeSlot', type: null, component: 'enum', enumName: ENUMS.YES_NO, multipleSelection: false },
	{
		name: 'branchFulfillment',
		type: null,
		component: 'enum',
		enumName: ENUMS.YES_NO,
		multipleSelection: false
	},
	{
		name: 'warehouseFulfillment',
		type: null,
		component: 'enum',
		enumName: ENUMS.YES_NO,
		multipleSelection: false
	},
	{
		name: 'predecessorAutomationId',
		type: null,
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.AUTOMATIONS,
		columns: [{ accessor: 'Name', width: 200 }],
		selection: ['Name']
	},
	{
		name: 'originCityGroup',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.CITY_GROUPS,
		columns: [{ accessor: 'Name', width: 200 }],
		selection: ['Name'],
		multipleSelection: true
	},
	{
		name: 'destinationCityGroup',
		type: 'equal',
		component: 'auto',
		autoCompleteName: AUTOCOMPLETE.CITY_GROUPS,
		columns: [{ accessor: 'Name', width: 200 }],
		selection: ['Name'],
		multipleSelection: true
	}
];

const validateOptions = {
	shouldValidate: true,
	shouldDirty: true
};

const CityByCountry = ({ index, disabled }) => {
	const formMethods = useFormContext();

	const { control, setValue } = formMethods;

	const cityName = useWatch({
		control,
		name: `conditions[${index}].value`
	});

	const countryName = useWatch({
		control,
		name: `conditions[${index - 1}].value`
	});
	const { cities, fetchCitiesByCountry } = useAddressData();

	useEffect(() => {
		if (countryName) {
			fetchCitiesByCountry(countryName.toString());
		}
	}, [countryName]);

	return (
		<EnumSelect
			name={`conditions[${index}].value`}
			value={cityName ?? null}
			setValue={(value) => {
				setValue(`conditions[${index}].value`, value ?? null, {
					shouldValidate: true,
					shouldDirty: true
				});
			}}
			disabled={disabled}
			data={cities}
			multipleSelection
		/>
	);
};

const CountrySelect = ({ index, disabled }) => {
	const formMethods = useFormContext();

	const { control, setValue } = formMethods;
	const { countries } = useAddressData();

	const countryName = useWatch({
		control,
		name: `conditions[${index}].value`
	});

	return (
		<EnumSelect
			labelPrefix="country."
			name={`conditions[${index}].value`}
			data={countries}
			value={countryName}
			setValue={(value) => {
				setValue(`conditions[${index}].value`, value ?? null, {
					shouldValidate: true,
					shouldDirty: true
				});
			}}
			disabled={disabled}
		/>
	);
};

export const convertAutomationConditionValue = (value, parameter) => {
	if (!parameter) return value;

	if (parameter?.component === 'string') {
		return value;
	}

	if (parameter?.component === 'integer') {
		return Number(value);
	}

	if (parameter?.component === 'enum') {
		return parameter.multipleSelection ? value?.split(',') : value;
	}

	if (parameter?.component === 'auto') {
		return parameter.multipleSelection
			? value
					?.split(',')
					.map((val) => Number(val))
					.sort()
			: Number(value);
	}

	if (parameter?.component === 'chips') {
		return value?.split(',');
	}

	if (parameter?.component === 'cityStr') {
		return value?.split(',');
	}

	if (parameter?.component === 'countryStr') {
		return value;
	}

	if (parameter?.component === 'time') {
		return value;
	}

	return value;
};

const InputValueComponent = ({ index, disabled }) => {
	const { control, setValue } = useFormContext();
	const watchedCondition = useWatch({
		control,
		name: `conditions[${index}].condition`
	});

	const watchedValue = useWatch({
		control,
		name: `conditions[${index}].value`
	});

	const watchedOperator = useWatch({
		control,
		name: `conditions[${index}].operator`
	});

	if (!watchedCondition) {
		return <StringInput name={`conditions[${index}].value`} disabled={disabled} />;
	}

	const parameter = AutomationConditionsConfig.find(
		(item) =>
			item.name === watchedCondition || item?.nameV1 === watchedCondition || item?.nameV12 === watchedCondition
	);

	if (parameter?.component === 'string') {
		return <StringInput name={`conditions[${index}].value`} required disabled={disabled} />;
	}

	if (parameter?.component === 'integer') {
		return (
			<IntegerInput
				required
				name={`conditions[${index}].value`}
				unit={parameter.measurementLabel}
				disabled={disabled}
			/>
		);
	}

	if (parameter?.component === 'enum') {
		return (
			<EnumSelect
				disabled={disabled}
				required
				name={`conditions[${index}].value`}
				multipleSelection={parameter.multipleSelection}
				enumName={parameter.enumName}
				setValue={(value) => {
					setValue(`conditions[${index}].value`, value ?? null, validateOptions);
				}}
				value={watchedValue}
			/>
		);
	}

	if (
		parameter?.component === 'chips' ||
		(watchedOperator === 'equalCustom' && parameter?.customComponent === 'chips')
	) {
		return (
			<Chips
				value={watchedValue}
				setValue={(value) => {
					setValue(`conditions[${index}].value`, value ?? null, validateOptions);
				}}
			/>
		);
	}

	if (parameter?.component === 'auto') {
		return (
			<AutoComplete
				disabled={disabled}
				required
				name={`conditions[${index}].value`}
				multipleSelection
				autoCompleteName={parameter.autoCompleteName}
				setValue={(value) => {
					setValue(
						`conditions[${index}].value`,
						value?.map((item) => item.ID),
						validateOptions
					);
				}}
				value={watchedValue}
				columns={parameter.columns}
				selectionLabelAccessor={parameter.selection}
			/>
		);
	}

	if (parameter?.component === 'cityStr') {
		return <CityByCountry index={index} disabled={disabled} />;
	}

	if (parameter?.component === 'countryStr') {
		return <CountrySelect index={index} disabled={disabled} />;
	}

	if (parameter?.component === 'time') {
		return (
			<TimeInput
				name={`conditions[${index}].value`}
				required
				disabled={disabled}
				setValue={(value) => {
					setValue(`conditions[${index}].value`, value, validateOptions);
				}}
				value={watchedValue}
			/>
		);
	}
};

const OperatorDropdown = ({ index, disabled }) => {
	const { control, setValue } = useFormContext();
	const watchedCondition = useWatch({
		control,
		name: `conditions[${index}].condition`
	});

	const watchedOperator = useWatch({
		control,
		name: `conditions[${index}].operator`
	});

	const parameter = useMemo(
		() =>
			AutomationConditionsConfig.find(
				(item) =>
					item.name === watchedCondition ||
					item?.nameV1 === watchedCondition ||
					item?.nameV12 === watchedCondition
			),
		[watchedCondition]
	);

	const enumData = useMemo(() => {
		switch (parameter?.type) {
			case 'equal':
				return ['equal', 'excluding'];
			case 'less':
				return ['lessThan', 'greaterThan'];
			case 'equalLess':
				return ['equal', 'lessThan', 'greaterThan'];
			case 'equalContaines':
				return ['equal', 'includes'];
			case 'equalCustom':
				return ['equal', 'excluding', 'equalCustom'];
			default:
				return [];
		}
	}, [watchedCondition, parameter]);

	return parameter?.type || !watchedCondition ? (
		<EnumSelect
			searchable={false}
			valueRemovable={false}
			disabled={disabled}
			name={`conditions[${index}].operator`}
			data={enumData}
			value={watchedOperator}
			setValue={(value) => {
				setValue(`conditions[${index}].operator`, value, validateOptions);
			}}
		/>
	) : (
		<></>
	);
};

const ConditionHeader = () => {
	const navigate = useNavigate();

	return (
		<S.HeaderContainer>
			<Typography fontWeight="500">feasibility.condition</Typography>
			<Tooltip
				timeout={100}
				placement="right"
				color={'var(--white)'}
				title={
					<div>
						<ModuleToolTipContainer
							lordIcon="https://storage.googleapis.com/tryoto-public/lordicon-compressed/border/upgradeRocket.json.gz"
							title="shippingAutomationModuleConditionTitle"
							description="shippingAutomationModuleConditionSubtitle"
						/>

						<Button
							className="mt-3 mx-auto"
							label="upgradePlan"
							variant="blue"
							onClick={() => {
								navigate('/oto-modules/advance-shipping-automation');
							}}
						/>
					</div>
				}>
				<Icon icon={faCircleInfo} />
			</Tooltip>
		</S.HeaderContainer>
	);
};

const AutomationConditionsTable = (props) => {
	const { isLoading, conditionsEnumName, viewMode, deletableLastRow } = props;

	const { setValue, watch, control } = useFormContext();

	const isAuthorized = useAccessControl();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const { showError } = useAlert();
	const navigate = useNavigate();

	const areaCorrector = useSelector((state) => state.user.generalSettings.dataCorrectorArea);

	const {
		fields: conditionsFields,
		append: conditionsAppend,
		remove: conditionsRemove
	} = useFieldArray({ control, name: 'conditions' });

	const removeItem = async ({ index }) => {
		conditionsRemove(index);
	};

	const columns = useMemo(
		() => [
			{
				header: '',
				id: 'ifand',
				size: isMobile ? 0 : 20,
				maxSize: 20,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<Typography>{index === 0 ? 'feasibility.if' : 'feasibility.and'}</Typography>
				)
			},
			{
				id: 'feasibility.condition',
				header: isAuthorized('EnterprisePackage') ? 'feasibility.condition' : () => <ConditionHeader />,
				accessorKey: 'condition',
				size: isMobile ? 70 : 100,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => (
					<EnumSelect
						required
						placeholder="selectOne"
						name={`conditions[${index}].condition`}
						value={watch(`conditions[${index}].condition`)}
						fixedContainerWidth={!isMobile}
						enumName={conditionsEnumName ?? ENUMS.FEASIBILITY_CONDITIONS_ENTERPRISE}
						setValue={(value) => {
							if (value === 'originCity') {
								setValue(`conditions[${index}].condition`, 'originCountry', {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`conditions[${index}].operator`, 'equal', {
									shouldValidate: true,
									shouldDirty: true
								});
								conditionsAppend({
									condition: 'originCity',
									operator: 'equal'
								});
							} else if (value === 'destinationCity') {
								setValue(`conditions[${index}].condition`, 'destinationCountry', {
									shouldValidate: true,
									shouldDirty: true
								});
								setValue(`conditions[${index}].operator`, 'equal', {
									shouldValidate: true,
									shouldDirty: true
								});
								conditionsAppend({
									condition: 'destinationCity',
									operator: 'equal'
								});
							} else {
								setValue(`conditions[${index}].condition`, value ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});

								// Order automation condition
								if (value === 'areaId') {
									!areaCorrector &&
										showError({
											header: 'areaCorrectorError.header',
											message: 'areaCorrectorError.message',
											link: 'goToAutocorrector',
											linkAction: () => {
												navigate(PATHS.AUTOCORRECTOR);
											}
										});
								}
							}

							setValue(`conditions[${index}].operator`, null);
							setValue(`conditions[${index}].value`, null);
						}}
						disabled={viewMode}
					/>
				)
			},
			{
				header: 'feasibility.operator',
				accessorKey: 'operator',
				maxSize: 50,
				size: 50,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => <OperatorDropdown index={index} disabled={viewMode} />
			},
			{
				header: 'feasibility.value',
				accessorKey: 'value',
				size: isMobile ? 60 : 130,
				showOnMobile: true,
				renderCell: ({ row: { index } }) => <InputValueComponent index={index} disabled={viewMode} />
			},
			...(!viewMode
				? [
						{
							id: 'deleteRow',
							size: 20,
							maxSize: 20,
							showOnMobile: true
						}
					]
				: [])
		],
		[isMobile, viewMode]
	);

	return (
		<Table
			loading={isLoading}
			data={conditionsFields}
			columns={columns}
			onRowDelete={(row) => {
				removeItem(row);
			}}
			onRowAdd={() => {
				conditionsAppend({});
			}}
			showAddNewRowButton={!viewMode}
			deletableLastRow={deletableLastRow}
			noRecordsFound={<></>}
		/>
	);
};

export default AutomationConditionsTable;
